import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';




// Styled components
const Section = styled.section`
  padding: 100px 10px;
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 40px auto;
  padding: 40px;
  border-radius: 15px;
  box-sizing: border-box;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 30px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const FormTitle = styled.h2`
  text-align: left;
  margin-bottom: 40px;
  color: #222;
  font-size: 2.8em;
  font-weight: 700;
  position: relative;

  span {
    color: #007BFF;
  }

  &:after {
    content: '';
    width: 140px;
    height: 3px;
    background-color: #007BFF;
    display: block;
    margin: 15px 90px 0;
  }

  @media (max-width: 480px) {
    font-size: 1.76em;
  }
`;

const FormElement = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 10px;
  font-weight: 600;
  color: #555;
  font-size: 15px;
`;

const Input = styled.input`
  padding: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fff;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: #007BFF;
    box-shadow: 0 0 8px rgba(0, 123, 255, 0.2);
  }

  @media (max-width: 476px) {
    width: 90%;
  }
`;

const TextArea = styled.textarea`
  padding: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fff;
  resize: vertical;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: #007BFF;
    box-shadow: 0 0 8px rgba(0, 123, 255, 0.2);
  }
`;

const FileInput = styled(Input).attrs({ type: 'file' })`
  padding: 0;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  padding: 16px;
  background-color: #007BFF;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  grid-column: span 2;
  transition: background-color 0.3s ease, transform 0.3s ease;

  &:hover {
    background-color: #0056b3;
    transform: translateY(-3px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    grid-column: span 1;
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const CareerForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume') {
      setFormData({ ...formData, resume: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Validation
    const { name, email, phone, message, resume } = formData;

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // Phone validation regex (only digits, 10-15 characters)
    const phoneRegex = /^\d{10,15}$/;
  
    // Maximum file size for the resume (5MB in bytes)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; 
  
    // Supported file types for resume
    const supportedFileTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  
    // Client-side validation
    if (name.trim() === '' || name.length < 3) {
      toast.error("Please enter a valid name (at least 3 characters).");
      return;
    }
  
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
  
    if (!phoneRegex.test(phone)) {
      toast.error("Please enter a valid phone number (only digits, 10-15 characters).");
      return;
    }
  
    if (message.trim() === '' || message.length < 10) {
      toast.error("Please enter a valid message (at least 10 characters).");
      return;
    }
  
    if (!resume) {
      toast.error("Please upload your resume.");
      return;
    }
  
    if (!supportedFileTypes.includes(resume.type)) {
      toast.error("Invalid file type. Only PDF, DOC, and DOCX are allowed.");
      return;
    }
  
    if (resume.size > MAX_FILE_SIZE) {
      toast.error("File size too large. Please upload a resume smaller than 5MB.");
      return;
    }
  
    // Create FormData to submit the form
    const form = new FormData();
    form.append('name', name);
    form.append('email', email);
    form.append('phone', phone);
    form.append('message', message);
    form.append('resume', resume);
  
    try {
      await axios.post('https://apis.nextechplm.com/career/add', form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      // Reset the form data
      setFormData({ name: '', email: '', phone: '', message: '', resume: null });
      toast.success('Form submitted successfully!');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('There was an error submitting the form.');
    }
    setIsSubmitting(false);
  };

  return (
    <Section className='container'>
      <FormTitle>Career <span>Application Form</span></FormTitle>
      <FormContainer>
        <FormElement onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Full Name</Label>
            <Input 
              type="text" 
              id="name" 
              name="name" 
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required 
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input 
              type="email" 
              id="email" 
              name="email" 
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              required 
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">Phone Number</Label>
            <Input 
              type="tel" 
              id="phone" 
              name="phone" 
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              required 
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">Message</Label>
            <TextArea 
              id="message" 
              name="message" 
              rows="6"
              value={formData.message}
              onChange={handleChange}
              placeholder="Write your message here"
              required 
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="resume">Upload Resume</Label>
            <FileInput 
              id="resume" 
              name="resume" 
              accept=".pdf,.doc,.docx"
              onChange={handleChange}
              ref={fileInputRef}
              required 
            />
          </FormGroup>

          <SubmitButton type="submit">Submit Application</SubmitButton>
        </FormElement>
      </FormContainer>
    </Section>
  );
};

export default CareerForm;
